import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toPastTenseAction } from '../../../utils/actionLabel'
import {
  Button,
  message,
  Modal,
  Spin,
  Tabs,
} from '@surecompanies/core_components'
import EditOutlined from '@ant-design/icons/EditOutlined'
import PlanLocationOverrideForm, {
  type LocationServerFieldError,
} from './PlanLocationOverrideForm'
import OverrideHeaderChips from '../../../components/OverrideHeaderChips'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import { apiPayloadToForm } from './planLocationOverrideForm.schema'
import type {
  CreatePlanLocationOverrideRuleRequestDto,
  UpdatePlanLocationOverrideRuleRequestDto,
} from '../../../api/dto/plan-location-override-rules.dto'
import { PlanLocationOverrideRuleStatus } from '../enums/plan-location-override-rule.enum'
import { useGetLocationOverrideRule } from '../hooks/useGetLocationOverrideRule'
import { useToggleLocationOverrideRuleStatus } from '../hooks/useToggleLocationOverrideRuleStatus'
import { useUpdateLocationOverrideRule } from '../hooks/useUpdateLocationOverrideRule'
import {
  extractFieldErrors,
  getApiErrorMessage,
  getApiErrorStatus,
} from '../../../utils/apiErrors'
import DocumentSection from '../../../components/DocumentSection'
import OverrideHistoryTimeline from '../../../components/OverrideHistoryTimeline'
import { useEditDocumentStaging } from '../../../hooks/useEditDocumentStaging'
import { LOCATION_OVERRIDE_RULE_BASE } from '../../../api/plan-location-override-rules-api'
import type { LocationViewMetadata } from './planLocationOverrideForm.schema'
import { formatLocationUpsertRule } from './rule-templates'

export type PlanLocationOverrideModalMode = 'view' | 'edit'

interface Props {
  ruleId: string | null
  initialMode?: PlanLocationOverrideModalMode
  onClose: () => void
  onSaved?: (status: PlanLocationOverrideRuleStatus) => void
  canEdit?: boolean
}

export default function PlanLocationOverrideModal({
  ruleId,
  initialMode = 'view',
  onClose,
  onSaved,
  canEdit = true,
}: Props) {
  const open = ruleId !== null
  const [mode, setMode] = useState<PlanLocationOverrideModalMode>(initialMode)
  const [activeTab, setActiveTab] = useState('details')
  const [serverErrors, setServerErrors] = useState<LocationServerFieldError[]>(
    []
  )
  const {
    data: rule,
    isLoading,
    error,
  } = useGetLocationOverrideRule(ruleId ?? undefined)
  const { mutateAsync, isPending: isUpdatePending } =
    useUpdateLocationOverrideRule(ruleId ?? '')
  const { mutateAsync: toggleStatus, isPending: isTogglePending } =
    useToggleLocationOverrideRuleStatus()
  const isPending = isUpdatePending || isTogglePending
  const [isSubmitLocked, setIsSubmitLocked] = useState(false)
  const submitLockRef = useRef(false)
  const staging = useEditDocumentStaging({
    ruleId: ruleId ?? '',
    ruleBase: LOCATION_OVERRIDE_RULE_BASE,
  })
  const isBusy = isPending || staging.isCommitting || isSubmitLocked

  useEffect(() => {
    if (open) {
      setMode(initialMode)
      setActiveTab('details')
      setServerErrors([])
    }
  }, [open, initialMode, ruleId])

  const stagingDiscard = staging.discard
  useEffect(() => {
    stagingDiscard()
  }, [ruleId, stagingDiscard])

  const defaultValues = useMemo(() => {
    if (!rule) return undefined
    return apiPayloadToForm(rule)
  }, [rule])

  const metadata = useMemo<LocationViewMetadata | undefined>(() => {
    if (!rule) return undefined
    return {
      status: rule.status,
      createdAt: rule.createdAt,
      createdByEmail: rule.createdByEmail,
      updatedAt: rule.updatedAt,
      updatedByEmail: rule.updatedByEmail,
    }
  }, [rule])

  const closeModal = () => {
    if (isBusy) return
    if (staging.hasStagedChanges) {
      Modal.confirm({
        title: 'Discard unsaved document changes?',
        content:
          'You have pending uploads or removals that have not been saved. Discard them?',
        okText: 'Discard',
        okButtonProps: { danger: true },
        cancelText: 'Keep editing',
        onOk: () => {
          staging.discard()
          setServerErrors([])
          onClose()
        },
      })
      return
    }
    setServerErrors([])
    onClose()
  }

  const handleSwitchToEdit = () => {
    setServerErrors([])
    setMode('edit')
  }

  const handleCancelEdit = closeModal

  const handleStatusChange = (toStatus: PlanLocationOverrideRuleStatus) => {
    const actionLabel =
      toStatus === PlanLocationOverrideRuleStatus.APPROVED
        ? 'Approve'
        : toStatus === PlanLocationOverrideRuleStatus.REJECTED
          ? 'Reject'
          : 'Re-draft'
    Modal.confirm({
      title: `${actionLabel} override`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()} this override?`,
      okText: actionLabel,
      okButtonProps: {
        danger: toStatus === PlanLocationOverrideRuleStatus.REJECTED,
      },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await toggleStatus({ id: ruleId ?? '', status: toStatus })
          message.success(`Override ${toPastTenseAction(actionLabel)}.`)
          if (rule) onSaved?.(toStatus)
          onClose()
        } catch {
          message.error(
            `Failed to ${actionLabel.toLowerCase()} override. Please try again.`
          )
        }
      },
    })
  }

  const handleApprove = () =>
    handleStatusChange(PlanLocationOverrideRuleStatus.APPROVED)
  const handleReject = () =>
    handleStatusChange(PlanLocationOverrideRuleStatus.REJECTED)
  const handleRedraft = () =>
    handleStatusChange(PlanLocationOverrideRuleStatus.DRAFT)

  const handleSubmit = async (
    body: CreatePlanLocationOverrideRuleRequestDto
  ) => {
    if (submitLockRef.current || isBusy) return
    submitLockRef.current = true
    setIsSubmitLocked(true)
    setServerErrors([])
    const updateBody: UpdatePlanLocationOverrideRuleRequestDto = {
      params: body.params,
      sourceUrl: body.sourceUrl ?? null,
      notes: body.notes ?? null,
    }
    try {
      await mutateAsync(updateBody)
      try {
        await staging.commit()
      } catch (commitErr) {
        // eslint-disable-next-line no-console
        console.error('staged document commit failed', commitErr)
        message.error(
          'Override saved, but some document changes failed. Please retry.'
        )
        return
      }
      message.success('Override updated.')
      if (rule) onSaved?.(rule.status)
      onClose()
    } catch (err) {
      const status = getApiErrorStatus(err)
      if (status === 400 || status === 422) {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors.length > 0) {
          setServerErrors(fieldErrors)
          return
        }
        message.error(
          getApiErrorMessage(
            'Some fields look incorrect. Please review and try again.'
          )
        )
      } else {
        // eslint-disable-next-line no-console
        console.error('updateLocationOverrideRule failed', err)
        message.error('An error occurred. Please try again.')
      }
    } finally {
      submitLockRef.current = false
      setIsSubmitLocked(false)
    }
  }

  const isViewMode = mode === 'view'
  const isApproved = rule?.status === PlanLocationOverrideRuleStatus.APPROVED
  const isDraft = rule?.status === PlanLocationOverrideRuleStatus.DRAFT

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className="bodyPlaceholder">
        <Spin />
      </div>
    )
  } else if (error || !rule) {
    body = (
      <div className="bodyPlaceholder">
        <p>Override not found.</p>
      </div>
    )
  } else if (isViewMode) {
    body = (
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <PlanLocationOverrideForm
                mode="view"
                defaultValues={defaultValues}
                extraContent={
                  ruleId ? (
                    <DocumentSection
                      ruleId={ruleId}
                      readOnly
                      ruleBase={LOCATION_OVERRIDE_RULE_BASE}
                    />
                  ) : null
                }
              />
            ),
          },
          {
            key: 'history',
            label: 'History',
            children: ruleId ? (
              <OverrideHistoryTimeline
                ruleId={ruleId}
                ruleBase={LOCATION_OVERRIDE_RULE_BASE}
                staticTemplateLabel="Update location data"
                paramsFormatter={formatLocationUpsertRule}
              />
            ) : null,
          },
        ]}
      />
    )
  } else {
    body = (
      <PlanLocationOverrideForm
        mode="edit"
        defaultValues={defaultValues}
        metadata={metadata}
        serverErrors={serverErrors}
        isSubmitting={isBusy}
        onSubmit={handleSubmit}
        onCancel={handleCancelEdit}
        extraContent={
          ruleId ? (
            <DocumentSection
              ruleId={ruleId}
              staging={staging}
              ruleBase={LOCATION_OVERRIDE_RULE_BASE}
            />
          ) : null
        }
      />
    )
  }

  const titleNode = (
    <div className="titleRow">
      <span className="titleLeft">
        <span className="titleText">
          {isViewMode ? 'Review Location Override' : 'Edit Location Override'}
        </span>
        {rule && <OverrideHeaderChips label="Locations" status={rule.status} />}
      </span>
    </div>
  )

  return (
    <Modal
      title={titleNode}
      open={open}
      onCancel={closeModal}
      width={960}
      destroyOnClose
      maskClosable={!isBusy}
      footer={
        isViewMode && rule && activeTab === 'details' ? (
          <div className="footerRow">
            {metadata && <OverrideAuditStrip metadata={metadata} />}
            <div className="footerActions">
              <Button type="default" onClick={closeModal} disabled={isBusy}>
                Close
              </Button>
              {canEdit && (
                <Button
                  type="default"
                  onClick={handleSwitchToEdit}
                  disabled={isBusy}
                >
                  <EditOutlined /> Edit
                </Button>
              )}
              {canEdit && (
                <>
                  {isApproved && (
                    <Button danger onClick={handleReject} loading={isPending}>
                      Reject
                    </Button>
                  )}
                  {!isApproved && (
                    <Button
                      type="primary"
                      onClick={handleApprove}
                      loading={isPending}
                    >
                      Approve
                    </Button>
                  )}
                  {!isDraft && (
                    <Button onClick={handleRedraft} loading={isPending}>
                      Re-draft
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {body}
    </Modal>
  )
}
